import * as React from "react"
import { Action, IOWidget, mk_pair, mk_widget, Widget } from "widgets-for-react"
import { CMSState, Link } from "../../../../cms/blocks/types.gen"
import { link } from "../../../../cms/blocks/utils.gen"
import { PublicSite_CustomPageData } from "../../../application.mod";

export type ExpandBlockProps<CustomBlockData, extra_images> = {
  index: number
  title?: string
  intro?: string
  items: ExpandProps<CustomBlockData, extra_images>[]
  link?: Link
  inview?: {
    threshold?: number
    triggerOnce?: boolean
  }
}

export type ExpandProps<CustomBlockData, extra_images> = {
  index: number
  key?: string
  title: string
  open: boolean
  useSVGIcons: boolean
  blocks: Array<string>
}

export function ExpandBlock<CustomBlockData extends { kind: CustomBlockData["kind"] }, extra_images>(
  props: ExpandBlockProps<CustomBlockData, extra_images>,
  options?: { key?: string }
): IOWidget<CMSState<CustomBlockData, extra_images, PublicSite_CustomPageData>, Action<CMSState<CustomBlockData, extra_images, PublicSite_CustomPageData>>> {
  return (s: CMSState<CustomBlockData, extra_images, PublicSite_CustomPageData>) =>
    mk_widget({
      run: onDone => (
        <section
          id={`io-${options && options.key ? options.key : props.index}`}
          className={`block block--expandables${props.title || props.intro ? ' block--background' : ''}`}
          key={options && options.key ? options.key : `block-${props.index}`}
        >
          <div className="block__inner container--s grid--block">

            {(props.title || props.intro) && (
              <header className="block__header">
                {props.title && <h2 className="block__title typo--3xl">{props.title}</h2>}
                {props.intro && <p className="block__intro" dangerouslySetInnerHTML={{__html: props.intro}}></p>}
              </header>
            )}

            <div className="block__content expanders">
              <ul className="expanders__list">
                {props.items.map((value, index) =>
                  Expand<CustomBlockData, extra_images>(
                    s,
                    {
                      index: props.index,
                      key: `${options && options.key ? options.key : props.index}-${index}`,
                      open: value.open,
                      title: value.title,
                      useSVGIcons: value.useSVGIcons,
                      blocks: value.blocks
                    },
                    index,
                    options
                  ).run(action => {
                    let openNew = action(value.open)
                    onDone(s1 => {
                      let currentBlock = s1.blocks.get(props.index) as any
                      if (currentBlock.kind === "expandableBlock") {
                        currentBlock = {
                          ...currentBlock,
                          value: mk_pair(
                            {
                              ...currentBlock.value.fst,
                              items: currentBlock.value.fst.items.map(
                                (e:any, i: number) => (i === index ? { ...e, open: openNew } : e)
                              ),
                            },
                            currentBlock.value.snd
                          ),
                        }
                      }
                      return { ...s1, blocks: s1.blocks.set(props.index, currentBlock) }
                    })
                  })
                )}
              </ul>
            </div>

            {props.link &&
              <footer className="block__footer">
                {link(
                  props.link,
                  { classname: 'button button--primary' },
                  props.link.title,
                  () => {
                    window.dataLayer.push({
                      event: 'event.dubbeldam',
                      category: 'ExpandableBlock',
                      action: props.link.title,
                      label: window.location.pathname,
                    })
                  }
                )}
              </footer>
            }
          </div>
        </section>
      ),
    })
}

function Expand<CustomBlockData extends { kind: CustomBlockData["kind"] }, extra_images>(
  s: CMSState<CustomBlockData, extra_images, PublicSite_CustomPageData>,
  props: ExpandProps<CustomBlockData, extra_images>,
  index: number,
  options?: {  key?: string }
): Widget<Action<boolean>> {
  return mk_widget({
    run: onDone =>
      <li  className="expander" key={`expand-${options && options.key ? options.key : props.index}-${index}`}>
        <button
          onClick={() => {
            onDone(s => !s)
            if (!props.open)
              window.dataLayer.push({
                event: "event.dubbeldam", category: "Expandable", action: props.title, label: window.location.pathname
              });
          }}
          className="expander__button"
          aria-controls={`a11y-${props.key}`}
          aria-expanded={props.open}
          aria-label="Toggle item"
        >
          <h3 className="expander__title typo--xs">{props.title}</h3>
          <span className="expander__icon" aria-hidden="true"></span>
        </button>

        <div
          id={`a11y-${props.key}`}
          className="expander__content"
          aria-hidden={!props.open}
        >
          <div className='expanded__content' dangerouslySetInnerHTML={{__html: props.blocks[0]}}/>
        </div>
    </li>
  })
}
